/** Recipe monitoring on the list. A missing field is monitored. */

export const RECIPE_LIST_STATUS = {
  ACTIVE: 'active',
  ARCHIVED: 'archived',
  ALL: 'all',
}

export const RECIPE_LIST_STATUS_OPTIONS = [
  { key: RECIPE_LIST_STATUS.ACTIVE, label: 'Monitored' },
  { key: RECIPE_LIST_STATUS.ARCHIVED, label: 'Stored' },
  { key: RECIPE_LIST_STATUS.ALL, label: 'All' },
]

export const RECIPE_ARCHIVED_ROW_LABEL = 'Stored — press Monitor to see the GP%'

/**
 * Monitoring is the paid watch. Storing keeps the last costs.
 * Monitored recipes are watched. Stored recipes stay saved and stay quiet.
 */
export const RECIPE_MONITORING_EXPLAINER = Object.freeze({
  title: 'Monitoring is the part the plan pays for',
  summary:
    'Monitored plates are recalculated when a supplier price changes, and the phones signed in here are told. The dashboard shows one alert when you sign in.',
  freeLine: 'Lite watches 5. Starter watches 50.',
  whyLabel: 'Why monitor?',
  body: 'A monitored recipe is recalculated when a supplier price changes, and the phones signed in to this venue are told the GP moved. The dashboard shows that alert once when you sign in, then stays quiet for that change. A stored recipe keeps the last selling price and ingredient costs. The GP% stays hidden until you press Monitor. Price changes on it stay quiet.',
  activeArchived:
    'Monitored means we are watching it. Stored means we have stopped. The recipe stays saved, and it stays off the alerts.',
  who: 'Alerts on the phone go to everyone signed in on the app. The dashboard shows one alert when you sign in. Add or remove people in Settings → Users.',
  monitoredDetail:
    'Monitored. When a supplier price changes, we recalculate this plate and tell the phones signed in to this venue. The dashboard shows one alert when you sign in. Add or remove people in Settings → Users.',
  unmonitoredDetail:
    'This recipe is stored, so the GP% is hidden. Press Monitor to see it. A supplier price change stays quiet, and nobody is told.',
})

/** Shown on Users. One sentence. No email list. */
export const RECIPE_ALERT_RECIPIENTS_NOTE =
  'Recipe alerts are phone notifications to everyone signed in on the app, and the dashboard shows one when you sign in. Removing a person stops their phone alerts.'

export const isRecipeArchived = recipe => recipe?.monitored === false

const isFreeRecipePlan = plan =>
  String(plan || 'Free')
    .trim()
    .toLowerCase() === 'free'

/** One line under the monitoring count. Free names the Starter watch. */
export const recipeMonitoringSummaryLine = (plan = 'Free') =>
  isFreeRecipePlan(plan)
    ? `${RECIPE_MONITORING_EXPLAINER.summary} ${RECIPE_MONITORING_EXPLAINER.freeLine}`
    : RECIPE_MONITORING_EXPLAINER.summary

export const recipeMonitoringDetailCopy = recipe =>
  isRecipeArchived(recipe)
    ? RECIPE_MONITORING_EXPLAINER.unmonitoredDetail
    : RECIPE_MONITORING_EXPLAINER.monitoredDetail

const byTitle = (a, b) =>
  String(a?.title || '').localeCompare(String(b?.title || ''))

/**
 * Empty recipe list. An archived list says so. Archived together with
 * GP Changed names both filters.
 */
export const formatRecipeListEmpty = ({
  listStatus = RECIPE_LIST_STATUS.ALL,
  gpChanged = false,
} = {}) => {
  const archived = listStatus === RECIPE_LIST_STATUS.ARCHIVED
  if (archived && gpChanged) {
    return 'Stored and GP Changed are both on. No recipes match.'
  }
  if (archived) return 'There are no stored recipes.'
  return 'No recipes found'
}

export const filterRecipesByListStatus = (
  recipes = [],
  status = RECIPE_LIST_STATUS.ALL,
) => {
  if (status === RECIPE_LIST_STATUS.ARCHIVED) {
    return recipes.filter(isRecipeArchived)
  }
  if (status === RECIPE_LIST_STATUS.ACTIVE) {
    return recipes.filter(recipe => !isRecipeArchived(recipe))
  }
  return recipes
}

/** Live recipes first. Archived recipes sit underneath, each group by title. */
export const splitLiveAndArchivedRecipes = (recipes = []) => {
  const live = []
  const archived = []
  recipes.forEach(recipe => {
    if (isRecipeArchived(recipe)) archived.push(recipe)
    else live.push(recipe)
  })
  live.sort(byTitle)
  archived.sort(byTitle)
  return { live, archived }
}

const countOfCap = (count, max) => {
  const n = Math.max(0, Number(count) || 0)
  const cap = max == null || max === '' ? null : Number(max)
  if (cap == null || Number.isNaN(cap)) return String(n)
  return `${n} of ${cap}`
}

const recipeTotalPhrase = (count, max) => {
  const counted = countOfCap(count, max)
  const n = Math.max(0, Number(count) || 0)
  if (n === 1 && !counted.includes(' of ')) return '1 recipe'
  return `${counted} recipes`
}

/**
 * Total, monitored, and stored (saved but not monitored).
 * “14 recipes · Monitoring 13 · 1 stored”.
 * A null cap is unlimited, so that side stays a plain count.
 * The store cap sits on the total. Stored has no cap of its own.
 */
export const formatRecipeMonitoringHeader = ({
  monitoredCount = 0,
  monitoredMax = null,
  storedCount = 0,
  storedMax = null,
} = {}) => {
  const stored = Math.max(
    0,
    (Number(storedCount) || 0) - (Number(monitoredCount) || 0),
  )
  return `${recipeTotalPhrase(
    storedCount,
    storedMax,
  )} · Monitoring ${countOfCap(
    monitoredCount,
    monitoredMax,
  )} · ${stored} stored`
}

/** Express archive copy includes a live-menu or parent-recipe sentence. */
export const archiveMessageHasWarning = message =>
  /still on |still contains|still uses|still use /i.test(String(message || ''))

export const recipeArchiveNotice = response => {
  const description = response?.message || RECIPE_ARCHIVED_ROW_LABEL
  return {
    message: 'Stored',
    description,
    duration: 8,
    warning: archiveMessageHasWarning(description),
  }
}

export const STARTER_WATCH_WHEN_FULL =
  'Starter watches 50, so the next plates stay watched.'

/** Free cap notices name Starter. A sentence already present is left once. */
export const withStarterWatchSentence = message => {
  const text = String(message || '').trim()
  if (!text || text.includes(STARTER_WATCH_WHEN_FULL)) return text
  if (
    !/Your (?:Free|OMS Lite|Lite) plan already monitors \d+ of \d+ recipes\.?$/i.test(
      text,
    )
  ) {
    return text
  }
  return `${text} ${STARTER_WATCH_WHEN_FULL}`
}

export const recipeRestoreNotice = response => {
  const blocked = response?.changed === false && response?.monitored === false
  return {
    blocked,
    message: withStarterWatchSentence(
      response?.message || (blocked ? '' : 'Recipe monitored again.'),
    ),
    duration: blocked ? 8 : 3,
  }
}

/** Present when save stored the recipe without monitoring it. */
export const recipeUnmonitoredSaveNotice = response => {
  if (response?.monitored !== false || !response?.message) return null
  return {
    message: withStarterWatchSentence(response.message),
    duration: 8,
  }
}

/** Archived dishes stay on the menu. Their cost and GP are left out. */
export const recipeShowsMenuEconomics = recipe => recipe?.monitored !== false

/** Menu cards count monitored dishes. An archived dish stays on the menu. */
export const countMonitoredMenuRecipes = items =>
  (items || []).filter(item => recipeShowsMenuEconomics(item?.recipe || item))
    .length

export const readRecipeAllowance = data => {
  if (data?.storedCount == null && data?.monitoredCount == null) return null
  return {
    storedCount: data.storedCount ?? 0,
    storedMax: data.storedMax ?? null,
    monitoredCount: data.monitoredCount ?? 0,
    monitoredMax: Object.prototype.hasOwnProperty.call(data, 'monitoredMax')
      ? data.monitoredMax
      : null,
    recipesToArchive: data.recipesToArchive ?? 0,
    allowanceMessage: data.allowanceMessage || null,
    currentPlan: data.currentPlan || null,
  }
}
